import { Button, Select, SelectItem } from '@nextui-org/react'
import { FC, useCallback, useMemo, useState } from 'react'
import ApiStateView from '../ApiState'
import LogiSearch from '../LogiSearch'
import LogiTable from '../LogiTable'
import SectionShell from '../SectionShell'
import { TbPackageImport } from 'react-icons/tb'
import SectionTitle from '../SectionTitle'
// import KpiCard from '../KpiCard'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { discrepanciesApi, ReceptionEstado, ReceptionStoreDto } from '../../api'
import { ToneKey } from '../../models'

const chipClasses = (tone: ToneKey): string => {
  if (tone === 'green') return 'bg-[#EAF3DE] text-[#3B6D11]'
  if (tone === 'amber') return 'bg-[#FEF3C7] text-[#92400E]'
  if (tone === 'rosa') return 'bg-[#FAECE7] text-[#993C1D]'
  if (tone === 'blue') return 'bg-[#E6F1FB] text-[#185FA5]'
  if (tone === 'lila') return 'bg-[#ede9fe] text-[#7c3aed]'
  return 'bg-[#f5f5f4] text-[#78716c]'
}

const ESTADO: Record<ReceptionEstado, { tone: ToneKey, label: string }> = {
  completo: { tone: 'green', label: 'Llegó todo' },
  con_discrepancia: { tone: 'rosa', label: 'Con discrepancia' },
  sin_abrir: { tone: 'amber', label: 'Sin abrir' },
  parcial: { tone: 'blue', label: 'Parcial' }
}

type Filtro = 'todas' | 'con_discrepancia' | 'sin_abrir' | 'completo'

const FILTROS: Array<{ key: Filtro, label: string }> = [
  { key: 'todas', label: 'Todas' },
  { key: 'con_discrepancia', label: 'Con discrepancia' },
  { key: 'sin_abrir', label: 'Sin abrir' },
  { key: 'completo', label: 'Completas' }
]

const columns = [
  { name: 'Tienda', uid: 'store' },
  { name: 'Cajas', uid: 'total_cajas', align: 'center' as const },
  { name: 'Enviados', uid: 'enviados', align: 'center' as const },
  { name: 'Recibidos', uid: 'recibidos', align: 'center' as const },
  { name: 'Discrepancias', uid: 'discrepancias', align: 'center' as const },
  { name: 'Estado', uid: 'estado' },
  { name: '', uid: 'actions' }
]

const RecepcionView: FC = () => {
  const idSeller = useSelectedSeller()
  const { goToDetail } = useLogisticBox()
  const [search, setSearch] = useState('')
  const [filtro, setFiltro] = useState<Filtro>('todas')

  const data = useApi(useCallback(async () => await discrepanciesApi.list(idSeller), [idSeller]), [idSeller])

  const rows = useMemo(() => {
    let r = (data.data ?? []).filter(t => t.store.toLowerCase().includes(search.toLowerCase()))
    if (filtro !== 'todas') r = r.filter(t => t.estado === filtro)
    return r
  }, [data.data, search, filtro])

  const counts = useMemo(() => {
    const all = data.data ?? []
    return {
      todas: all.length,
      con_discrepancia: all.filter(t => t.estado === 'con_discrepancia').length,
      sin_abrir: all.filter(t => t.estado === 'sin_abrir').length,
      completo: all.filter(t => t.estado === 'completo').length
    }
  }, [data.data])

  // Sección de KPIs comentada en el render · cálculo comentado para no romper el build (noUnusedLocals)
  // const kpis: KpiCardData[] = useMemo(() => {
  //   const all = data.data ?? []
  //   return [
  //     { value: all.length, label: 'Tiendas con recepciones', tone: 'lila' as ToneKey },
  //     { value: counts.completo, label: 'Llegó todo', tone: 'green' as ToneKey },
  //     { value: counts.sin_abrir, label: 'Sin abrir', tone: 'amber' as ToneKey },
  //     { value: counts.con_discrepancia, label: 'Con discrepancia', tone: 'rosa' as ToneKey }
  //   ]
  // }, [data.data, counts])

  const renderCell = (t: ReceptionStoreDto, columnKey: string): JSX.Element => {
    switch (columnKey) {
      case 'store':
        return <span className='font-medium text-xs text-neutral-800'>{t.store}</span>
      case 'total_cajas':
        return (
          <span className='text-xs'>
            {t.total_cajas}
            {t.cajas_sin_abrir > 0 && <span className='text-[10px] text-[#92400E] ml-1'>({t.cajas_sin_abrir} sin abrir)</span>}
          </span>
        )
      case 'enviados':
        return <span className='text-xs'>{t.enviados}</span>
      case 'recibidos':
        return <span className='text-xs font-semibold text-[#3B6D11]'>{t.recibidos}</span>
      case 'discrepancias':
        return (
          <span className='text-xs font-semibold' style={{ color: t.discrepancias > 0 ? '#993C1D' : undefined }}>
            {t.discrepancias > 0 ? t.discrepancias : '—'}
          </span>
        )
      case 'estado': {
        const st = ESTADO[t.estado]
        return <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${chipClasses(st.tone)}`}>{st.label}</span>
      }
      case 'actions':
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            <Button
              size='sm' className='bg-[#44526B] text-white text-xs h-8'
              onPress={() => goToDetail('recepcion-detalle', t.store, t.id_store)}
            >
              Ver detalle
            </Button>
          </div>
        )
      default:
        return <span className='text-xs'>{(t as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      {/* <section className='grid grid-cols-2 md:grid-cols-4 gap-4'>
        {kpis.map((k, i) => <KpiCard key={i} data={k} />)}
      </section> */}

      <SectionShell title='Arribos' icon={TbPackageImport} noPadding>
        <div className='flex items-center justify-between flex-wrap gap-3 px-5 pt-5 pb-3'>
          <SectionTitle
            title='Recepción de cajas'
            description='Lo que llegó a cada tienda · si la caja se abrió y qué se recibió'
          />
          <div className='flex items-center gap-2 flex-wrap'>
            <Select
              aria-label='Filtrar recepciones por estado'
              size='sm'
              selectedKeys={[filtro]}
              disallowEmptySelection
              selectionMode='single'
              className='w-48'
              onSelectionChange={(keys) => {
                const k = Array.from(keys)[0]
                if (k !== undefined) setFiltro(k as Filtro)
              }}
            >
              {FILTROS.map(f => (
                <SelectItem key={f.key} textValue={`${f.label} (${counts[f.key]})`}>
                  {f.label} <span className='opacity-60'>({counts[f.key]})</span>
                </SelectItem>
              ))}
            </Select>
            <LogiSearch className='w-56' placeholder='Buscar tienda…' value={search} onChange={setSearch} />
          </div>
        </div>

        <div className='px-5 pb-5'>
          <ApiStateView
            loading={data.loading} error={data.error} onRetry={data.reload}
            empty={!data.loading && rows.length === 0} emptyText='Sin recepciones en esta vista'
          >
            <LogiTable
              ariaLabel='Recepción de cajas por tienda'
              columns={columns}
              items={rows}
              rowKey={(t: ReceptionStoreDto) => t.id_store}
              loading={data.loading}
              emptyText='Sin recepciones en esta vista'
              rowsPerPage={10}
              renderCell={renderCell}
              bottomExtra={<div className='text-xs text-neutral-500'>Mostrando {rows.length} tiendas</div>}
            />
          </ApiStateView>
        </div>
      </SectionShell>
    </div>
  )
}

export default RecepcionView
